import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts"

import { ReportingChartFrame } from "./chart-frame"
import { ReportingChartTooltip } from "./chart-tooltip"
import { categoricalColor } from "./theme"

export interface ReportingDonutSlice {
	id: string
	name: string
	value: number
	color?: string
}

interface ReportingDonutChartProps {
	title: string
	description?: string
	data: ReportingDonutSlice[]
	isLoading?: boolean
	className?: string
	height?: number
}

const DONUT_STROKE_PX = 12

export const ReportingDonutChart = ({
	title,
	description,
	data,
	isLoading,
	className,
	height = 220,
}: ReportingDonutChartProps) => {
	const slices = data.map((d, i) => ({
		...d,
		color: d.color ?? categoricalColor(i),
	}))
	const total = slices.reduce((sum, s) => sum + s.value, 0)
	const outerRadius = Math.max(48, Math.floor(height * 0.42))
	const innerRadius = Math.max(0, outerRadius - DONUT_STROKE_PX)

	return (
		<ReportingChartFrame
			title={title}
			description={description}
			isLoading={isLoading}
			isEmpty={slices.every((s) => s.value === 0)}
			className={className}
		>
			<div className="flex flex-col gap-4 sm:flex-row sm:items-center">
				<div
					className="w-full shrink-0 sm:w-1/2 [&_*]:outline-none"
					style={{ height }}
				>
					<ResponsiveContainer width="100%" height="100%">
						<PieChart>
							<Pie
								data={slices}
								dataKey="value"
								nameKey="name"
								innerRadius={innerRadius}
								outerRadius={outerRadius}
								paddingAngle={0}
								cornerRadius={0}
								minAngle={0}
								strokeWidth={0}
								isAnimationActive={false}
							>
								{slices.map((entry) => (
									<Cell key={entry.id} fill={entry.color} stroke="none" />
								))}
							</Pie>
							<Tooltip content={<ReportingChartTooltip />} />
						</PieChart>
					</ResponsiveContainer>
				</div>
				<ul className="min-w-0 flex-1 space-y-2 pb-1">
					{slices.map((slice) => {
						const pct =
							total > 0 ? Math.round((slice.value / total) * 1000) / 10 : 0
						return (
							<li
								key={slice.id}
								className="flex items-center justify-between gap-3 text-sm"
							>
								<div className="flex min-w-0 items-center gap-2">
									<span
										className="h-2.5 w-2.5 shrink-0 rounded-full"
										style={{ backgroundColor: slice.color }}
									/>
									<span className="truncate text-primary">{slice.name}</span>
								</div>
								<span className="shrink-0 tabular-nums text-secondary">
									{slice.value} <span className="text-tertiary">({pct}%)</span>
								</span>
							</li>
						)
					})}
				</ul>
			</div>
		</ReportingChartFrame>
	)
}
